/* Master Order — Календарь (Sprint Calendar-MVP)
 *
 * Read-only aggregation of ALREADY-EXISTING data. NOT a new event system.
 * Sources:
 *   - GET /api/master-order/orders?date_from&date_to
 *       → Order.event_date + optional event_time
 *       → Order.handed_over_at (delivered) when set
 *       → Order.delivery.date + delivery.time_window (pickup/courier/etc.)
 *   - GET /api/master-order/production-tasks?date_from&date_to
 *       → ProductionTask.scheduled_date + optional start_time
 *
 * NO new backend, NO new schemas, NO manual event creation, NO drag-and-drop,
 * NO Google sync, NO editing from calendar. Clicking any event opens the
 * EXISTING order detail at /master-order/orders/{id}.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, ShoppingBag, Truck, Boxes,
} from "lucide-react";
import MOShell, {
  useMOAccess, LoadingBlock, NoAccessScreen,
} from "@/components/masterOrder/MOShell";
import {
  listOrders, listProductionTasks, deliveryLabel,
} from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/* ---------- date helpers (ISO YYYY-MM-DD everywhere) ------------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayISO = () => isoDay(new Date());
const parseISO = (s) => new Date(s + "T00:00:00");
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
// ISO week starts Monday
const startOfWeek = (d) => {
  const c = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = (c.getDay() + 6) % 7; // 0=Mon .. 6=Sun
  c.setDate(c.getDate() - dow);
  return c;
};
const endOfWeek = (d) => addDays(startOfWeek(d), 6);
const sameYMD = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const RU_MONTHS_NOM = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
const RU_MONTHS_GEN = ["января","февраля","марта","апреля","мая","июня","июля","августа","сентября","октября","ноября","декабря"];
const RU_DOW = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];

const fmtRange = (from, to) => {
  const a = parseISO(from), b = parseISO(to);
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
    return `${a.getDate()}–${b.getDate()} ${RU_MONTHS_GEN[a.getMonth()]} ${a.getFullYear()}`;
  }
  return `${a.getDate()} ${RU_MONTHS_GEN[a.getMonth()]} — ${b.getDate()} ${RU_MONTHS_GEN[b.getMonth()]} ${b.getFullYear()}`;
};
const fmtTime = (t) => {
  if (!t) return "";
  // Backend sends time as "HH:MM:SS" or ISO — strip seconds
  const m = String(t).match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
};
const fmtDayHeader = (iso) => {
  const d = parseISO(iso);
  return `${d.getDate()} ${RU_MONTHS_GEN[d.getMonth()]} · ${RU_DOW[(d.getDay() + 6) % 7]}`;
};

/* ---------- viewport awareness ---------------------------------------- */
function useIsCompact() {
  const [compact, setCompact] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const listener = (e) => setCompact(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", listener);
    else mq.addListener(listener);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", listener);
      else mq.removeListener(listener);
    };
  }, []);
  return compact;
}

/* ---------- event model -------------------------------------------------
 * Every event carries: { id, day (iso), time (HH:MM|""), kind, title, subtitle, orderId }
 * kind ∈ { order, handoff, delivery, production }
 * ---------------------------------------------------------------------- */
const KIND_META = {
  order:      { color: "#8B6F4B", label: "Заказ" },
  handoff:    { color: "#4A7A55", label: "Выдача" },
  delivery:   { color: "#4A7A55", label: "Доставка" },
  production: { color: "#B26B4A", label: "Производство" },
};

const isHandoffDeliveryType = (t) => t === "pickup"; // Customer picks up = «выдача»
const isConfectionerDelivery = (t) => !!t && t !== "pickup"; // Courier / doorstep / post → «доставка»

function buildOrderEvents(order) {
  // Sprint MO-UX-Simplify: cancelled orders should not clutter the calendar.
  if (order.business_status === "cancelled") return [];
  const events = [];
  const orderId = order.id;
  const num = String(order.order_number || "").replace(/^0+/, "") || "";
  const title = num ? `Заказ №${num}` : "Заказ";
  // 1) Main event = order.event_date (кондитерское событие)
  if (order.event_date) {
    events.push({
      id: `ord-${orderId}`,
      day: order.event_date,
      time: fmtTime(order.event_time),
      kind: "order",
      title,
      subtitle: order.event_type || null,
      orderId,
    });
  }
  // 2) Handoff / delivery — pulled from delivery subdoc when present
  //    We never invent a date; if delivery.date is absent, skip.
  const d = order.delivery;
  if (d && d.date) {
    const isHandoff = isHandoffDeliveryType(d.type);
    events.push({
      id: `dlv-${orderId}`,
      day: d.date,
      time: (d.time_window || "").trim() || null,
      kind: isHandoff ? "handoff" : "delivery",
      title: isHandoff ? `Выдача · ${title}` : `Доставка · ${title}`,
      subtitle: d.type ? deliveryLabel(d.type) : null,
      orderId,
    });
  }
  return events;
}

function buildTaskEvent(task) {
  return {
    id: `pt-${task.id}`,
    day: task.scheduled_date,
    time: fmtTime(task.start_time),
    kind: "production",
    title: task.title || "Этап",
    subtitle: null,
    orderId: task.order_id,
  };
}

/* ---------- calendar grid computation ---------------------------------- */
function monthGridRange(anchor) {
  // Anchor day → 6-week grid starting on Monday
  const first = startOfMonth(anchor);
  const gridStart = startOfWeek(first);
  const gridEnd = addDays(gridStart, 41); // 6 rows × 7 days - 1
  return { gridStart, gridEnd, monthIndex: anchor.getMonth() };
}
function weekGridRange(anchor) {
  const gridStart = startOfWeek(anchor);
  const gridEnd = endOfWeek(anchor);
  return { gridStart, gridEnd };
}

/* ------------------------------------------------------------------------
 * Component
 * ---------------------------------------------------------------------- */
export default function MOCalendar() {
  const { mode, loading: accessLoading } = useMOAccess();
  const nav = useNavigate();
  const compact = useIsCompact();

  const [view, setView] = useState("month"); // 'month' | 'week'
  const [anchor, setAnchor] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [events, setEvents] = useState([]); // flat list within the visible range
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(todayISO());

  const range = useMemo(
    () => view === "month" ? monthGridRange(anchor) : weekGridRange(anchor),
    [view, anchor],
  );
  const dateFrom = isoDay(range.gridStart);
  const dateTo = isoDay(range.gridEnd);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [orders, tasks] = await Promise.all([
        listOrders({ date_from: dateFrom, date_to: dateTo }).catch(() => []),
        listProductionTasks({ date_from: dateFrom, date_to: dateTo }).catch(() => []),
      ]);
      // Some orders in the range because of event_date but their delivery date may
      // lie OUTSIDE the visible window — that's OK, we simply filter in-window.
      const raw = [
        ...orders.flatMap(buildOrderEvents),
        ...tasks.map(buildTaskEvent),
      ].filter((e) => e.day && e.day >= dateFrom && e.day <= dateTo);
      // Deterministic sort: day asc, then time asc (empty time last), then title
      raw.sort((a, b) => {
        if (a.day !== b.day) return a.day < b.day ? -1 : 1;
        const at = a.time || "~", bt = b.time || "~";
        if (at !== bt) return at < bt ? -1 : 1;
        return String(a.title).localeCompare(String(b.title), "ru");
      });
      setEvents(raw);
    } catch (e) {
      toast.error(formatApiError(e));
      setEvents([]);
    } finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Group events by day for O(1) lookup during grid render
  const byDay = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      if (!map.has(e.day)) map.set(e.day, []);
      map.get(e.day).push(e);
    }
    return map;
  }, [events]);

  /* ---------- navigation ---------------------------------------------- */
  const goPrev = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
    else setAnchor(addDays(anchor, -7));
  };
  const goNext = () => {
    if (view === "month") setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
    else setAnchor(addDays(anchor, 7));
  };
  const goToday = () => {
    const t = new Date(); t.setHours(0, 0, 0, 0);
    setAnchor(t);
    setSelectedDay(todayISO());
  };

  const periodLabel = useMemo(() => {
    if (view === "month") return `${RU_MONTHS_NOM[anchor.getMonth()]} ${anchor.getFullYear()}`;
    return fmtRange(dateFrom, dateTo);
  }, [view, anchor, dateFrom, dateTo]);

  /* ---------- render --------------------------------------------------- */
  if (accessLoading) return <MOShell active="calendar" title="Календарь"><LoadingBlock /></MOShell>;
  if (mode === "no_access") return <NoAccessScreen />;

  const dayList = byDay.get(selectedDay) || [];

  return (
    <MOShell active="calendar" title="Календарь" subtitle="События из ваших заказов и производства.">
      <div data-testid="mo-calendar" className="pb-16">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="inline-flex rounded-full p-0.5"
               style={{ background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-calendar-view-switch">
            <ViewChip active={view === "month"} onClick={() => setView("month")} testid="mo-calendar-view-month">Месяц</ViewChip>
            <ViewChip active={view === "week"}  onClick={() => setView("week")}  testid="mo-calendar-view-week">Неделя</ViewChip>
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <IconBtn onClick={goPrev} testid="mo-calendar-prev" aria-label="Предыдущий период">
              <ChevronLeft size={14} />
            </IconBtn>
            <button onClick={goToday}
                    className="h-8 px-3 rounded-full text-[12.5px]"
                    style={{ background: "#F7F1E7", color: "#4A3728",
                             boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                    data-testid="mo-calendar-today">
              Сегодня
            </button>
            <IconBtn onClick={goNext} testid="mo-calendar-next" aria-label="Следующий период">
              <ChevronRight size={14} />
            </IconBtn>
          </div>
        </div>

        <div className="text-[15px] mb-3"
             style={{ fontFamily: "'Cormorant Garamond', serif", color: "#2A1D14" }}
             data-testid="mo-calendar-period-label">
          {periodLabel}
        </div>

        {loading ? (
          <LoadingBlock />
        ) : view === "month" ? (
          <MonthGrid range={range} byDay={byDay}
                     compact={compact}
                     selectedDay={selectedDay}
                     onSelectDay={setSelectedDay}
                     onOpenOrder={(orderId) => nav(`/master-order/orders/${orderId}`)} />
        ) : (
          <WeekList range={range} byDay={byDay}
                    onOpenOrder={(orderId) => nav(`/master-order/orders/${orderId}`)} />
        )}

        {/* Mobile: detailed day list under the month grid.
            (Desktop grid already shows expanded rows; this is for compact only.) */}
        {view === "month" && compact && (
          <div className="mt-4" data-testid="mo-calendar-day-list">
            <div className="text-[10.5px] tracking-[0.16em] uppercase mb-1.5" style={{ color: "#8B6F4B" }}>
              {fmtDayHeader(selectedDay)}
            </div>
            {dayList.length === 0 ? (
              <div className="rounded-lg p-3 text-[12.5px]" style={{ color: "#8B6F4B",
                   background: "#F7F1E7", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
                   data-testid="mo-calendar-day-empty">
                На этот день событий нет
              </div>
            ) : (
              <div className="space-y-1.5">
                {dayList.map((e) => (
                  <EventRow key={e.id} event={e}
                            onOpen={() => nav(`/master-order/orders/${e.orderId}`)} />
                ))}
              </div>
            )}
          </div>
        )}

        {events.length === 0 && !loading && !compact && (
          <div className="mt-6 rounded-lg p-4 text-[13px]"
               style={{ color: "#8B6F4B", background: "#F7F1E7",
                        boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid="mo-calendar-empty">
            На этот период событий нет.
          </div>
        )}
      </div>
    </MOShell>
  );
}

/* ------------------------- Month grid ---------------------------------- */
function MonthGrid({ range, byDay, compact, selectedDay, onSelectDay, onOpenOrder }) {
  const { gridStart, monthIndex } = range;
  const cells = useMemo(() => {
    const out = [];
    for (let i = 0; i < 42; i++) out.push(addDays(gridStart, i));
    return out;
  }, [gridStart]);
  const today = todayISO();

  return (
    <div className="rounded-lg overflow-hidden"
         style={{ boxShadow: "inset 0 0 0 1px #EFE4D0", background: "#FFFFFF" }}
         data-testid="mo-calendar-month-grid">
      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[10.5px] tracking-[0.14em] uppercase"
           style={{ color: "#8B6F4B", background: "#F7F1E7",
                    borderBottom: "1px solid #EFE4D0" }}>
        {RU_DOW.map((n) => (
          <div key={n} className="py-1.5 text-center">{n}</div>
        ))}
      </div>

      {/* 6 × 7 cells */}
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const iso = isoDay(d);
          const dayEvents = byDay.get(iso) || [];
          const isCurrentMonth = d.getMonth() === monthIndex;
          const isToday = iso === today;
          const isSelected = iso === selectedDay;
          return (
            <button key={iso}
                    onClick={() => {
                      onSelectDay(iso);
                      // On desktop with expanded rows, clicking a single event should
                      // still open it directly. Here the click on the cell just
                      // selects a day (for the compact list below).
                    }}
                    className="text-left align-top p-1.5 min-h-[64px] md:min-h-[92px] relative transition"
                    style={{
                      background: isSelected ? "#FBF3E4" : "transparent",
                      borderRight: (i % 7 === 6) ? "none" : "1px solid #EFE4D0",
                      borderBottom: (i >= 35) ? "none" : "1px solid #EFE4D0",
                      opacity: isCurrentMonth ? 1 : 0.4,
                      cursor: "pointer",
                    }}
                    data-testid={`mo-calendar-cell-${iso}`}>
              <div className="flex items-center gap-1 mb-0.5">
                <span className="text-[11.5px] tabular-nums"
                      style={{ color: isToday ? "#F7EFE0" : "#2A1D14",
                               background: isToday ? "#4A3728" : "transparent",
                               borderRadius: 9999, padding: isToday ? "1px 6px" : 0,
                               fontWeight: isToday ? 600 : 400 }}>
                  {d.getDate()}
                </span>
                {compact && dayEvents.length > 0 && (
                  <span className="ml-auto text-[10px] tabular-nums px-1.5 py-0.5 rounded-full"
                        style={{ background: "#EADFCF", color: "#4A3728" }}
                        data-testid={`mo-calendar-count-${iso}`}>
                    {dayEvents.length}
                  </span>
                )}
              </div>
              {/* Desktop: show up to 3 event chips. Mobile: rely on the count pill + day list. */}
              {!compact && (
                <div className="space-y-0.5" data-testid={`mo-calendar-events-${iso}`}>
                  {dayEvents.slice(0, 3).map((e) => (
                    <div key={e.id}
                         onClick={(ev) => { ev.stopPropagation(); onOpenOrder(e.orderId); }}
                         className="text-[10.5px] truncate rounded px-1 py-0.5 cursor-pointer hover:opacity-80"
                         title={e.title}
                         style={{ background: "#F7F1E7", color: "#2A1D14",
                                  boxShadow: `inset 2px 0 0 0 ${KIND_META[e.kind].color}` }}
                         data-testid={`mo-calendar-event-${e.id}`}>
                      {e.time && <span style={{ color: "#8B6F4B" }}>{e.time} </span>}
                      {e.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-[10.5px]" style={{ color: "#8B6F4B" }}>
                      +{dayEvents.length - 3}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------- Week list view ------------------------------ */
function WeekList({ range, byDay, onOpenOrder }) {
  const { gridStart } = range;
  const days = useMemo(() => {
    const out = [];
    for (let i = 0; i < 7; i++) out.push(addDays(gridStart, i));
    return out;
  }, [gridStart]);
  const today = todayISO();

  const hasAny = days.some((d) => (byDay.get(isoDay(d)) || []).length > 0);

  if (!hasAny) {
    return (
      <div className="rounded-lg p-4 text-[13px]"
           style={{ color: "#8B6F4B", background: "#F7F1E7",
                    boxShadow: "inset 0 0 0 1px #EFE4D0" }}
           data-testid="mo-calendar-empty">
        На этот период событий нет.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="mo-calendar-week-list">
      {days.map((d) => {
        const iso = isoDay(d);
        const dayEvents = byDay.get(iso) || [];
        const isToday = iso === today;
        return (
          <div key={iso} className="rounded-lg p-2.5 md:p-3"
               style={{ background: "#FFFFFF", boxShadow: "inset 0 0 0 1px #EFE4D0" }}
               data-testid={`mo-calendar-week-day-${iso}`}>
            <div className="flex items-baseline gap-2 mb-1.5">
              <div className="text-[13px] tabular-nums"
                   style={{ color: isToday ? "#F7EFE0" : "#2A1D14",
                            background: isToday ? "#4A3728" : "transparent",
                            borderRadius: 9999, padding: isToday ? "1px 8px" : 0,
                            fontFamily: "'Cormorant Garamond', serif" }}>
                {d.getDate()} {RU_MONTHS_GEN[d.getMonth()]}
              </div>
              <div className="text-[10.5px] tracking-[0.14em] uppercase"
                   style={{ color: "#8B6F4B" }}>
                {RU_DOW[(d.getDay() + 6) % 7]}
              </div>
            </div>
            {dayEvents.length === 0 ? (
              <div className="text-[12px]" style={{ color: "#B9A692" }}>—</div>
            ) : (
              <div className="space-y-1">
                {dayEvents.map((e) => (
                  <EventRow key={e.id} event={e} onOpen={() => onOpenOrder(e.orderId)} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------- Event row (list) ---------------------------- */
function EventRow({ event, onOpen }) {
  const meta = KIND_META[event.kind];
  const Icon = event.kind === "production" ? Boxes
    : (event.kind === "delivery" || event.kind === "handoff") ? Truck
    : ShoppingBag;
  return (
    <button onClick={onOpen}
            className="w-full text-left rounded-md px-2 py-1.5 flex items-center gap-2 hover:opacity-90"
            style={{ background: "#F7F1E7", boxShadow: `inset 3px 0 0 0 ${meta.color}` }}
            data-testid={`mo-calendar-event-row-${event.id}`}>
      <Icon size={13} style={{ color: meta.color, flexShrink: 0 }} />
      <span className="text-[12.5px] truncate flex-1" style={{ color: "#2A1D14" }}>
        {event.title}
      </span>
      {event.time && (
        <span className="text-[11.5px] tabular-nums whitespace-nowrap"
              style={{ color: "#8B6F4B" }}>
          {event.time}
        </span>
      )}
    </button>
  );
}

/* ------------------------- small primitives ---------------------------- */
function ViewChip({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick}
            className="h-8 px-3 rounded-full text-[12.5px] transition"
            style={{
              background: active ? "#4A3728" : "transparent",
              color: active ? "#F7EFE0" : "#4A3728",
            }}
            data-testid={testid}>
      {children}
    </button>
  );
}
function IconBtn({ onClick, children, testid, ...rest }) {
  return (
    <button onClick={onClick}
            className="w-8 h-8 rounded-full grid place-items-center"
            style={{ background: "#F7F1E7", color: "#4A3728",
                     boxShadow: "inset 0 0 0 1px #EFE4D0" }}
            data-testid={testid} {...rest}>
      {children}
    </button>
  );
}
